/** Ab dieser Zeilenzahl zeichnet die Kalkulation nur das sichtbare Fenster. */
export const CALCULATION_VIRTUALIZE_MIN_ROWS = 80;

/**
 * Dokumentzeilen sind schwerer (Textareas, Layout, Drop-Zonen) — früher virtualisieren
 * als in der Kalkulationstabelle.
 */
export const DOCUMENT_VIRTUALIZE_MIN_ROWS = 40;

/** Zusätzlicher Bereich über und unter dem Viewport, damit schnelles Scrollen nicht leer flackert. */
export const CALCULATION_ROW_OVERSCAN_PX = 480;

export type CalculationRowWindow = {
  start: number;
  end: number;
  paddingTop: number;
  paddingBottom: number;
};

/** `heights[i]` ist die Höhe von Zeile i inklusive der Einfügezeile davor. `prefix[i]` ist der Start von Zeile i. */
export function prefixSums(heights: readonly number[]): number[] {
  const prefix = new Array<number>(heights.length + 1);
  prefix[0] = 0;
  for (let i = 0; i < heights.length; i += 1) {
    prefix[i + 1] = prefix[i] + heights[i];
  }
  return prefix;
}

/**
 * Sichtbares Zeilenfenster in der scrollenden Kalkulationstabelle.
 * `scrollTop`/`viewportHeight` beziehen sich auf den Scroll-Container.
 * `headerHeight` ist die Tabellenkopfzeile, die im Fluss vor dem Body steht.
 * `trailing` ist die Einfügezeile hinter der letzten Position.
 */
export function visibleCalculationRowWindow(options: {
  count: number;
  heights: readonly number[];
  trailing: number;
  scrollTop: number;
  viewportHeight: number;
  headerHeight: number;
  overscanPx: number;
}): CalculationRowWindow {
  const { count, heights, trailing, scrollTop, viewportHeight, headerHeight, overscanPx } = options;
  const prefix = prefixSums(heights);
  if (count === 0) {
    return { start: 0, end: 0, paddingTop: 0, paddingBottom: Math.max(0, trailing) };
  }

  const viewStart = scrollTop - headerHeight - overscanPx;
  const viewEnd = scrollTop + viewportHeight - headerHeight + overscanPx;

  let lo = 0;
  let hi = count;
  while (lo < hi) {
    const mid = (lo + hi) >> 1;
    if (prefix[mid + 1] <= viewStart) lo = mid + 1;
    else hi = mid;
  }
  const start = lo;

  lo = start;
  hi = count;
  while (lo < hi) {
    const mid = (lo + hi) >> 1;
    if (prefix[mid] < viewEnd) lo = mid + 1;
    else hi = mid;
  }
  const end = lo;

  const paddingTop = prefix[start];
  const paddingBottom = prefix[count] - prefix[end] + (end === count ? 0 : trailing);
  return { start, end, paddingTop, paddingBottom };
}

/** Gemessene Zeilenschlitze für Drop-Index, wenn nicht alle Zeilen im DOM sind. */
export type OfferTableRowLayout = {
  count: number;
  prefix: number[];
  heights: number[];
  bandHeight: number;
  /** Oberkante des `tbody` im Scroll-Container, in CSS-Pixeln (inkl. Scale). */
  bodyOffsetScaled: number;
  scale: number;
};

/**
 * Einfügeindex in unskalierten Zeilenhöhen.
 * Jeder Schlitz ist `band + row`; Treffer in der Band- oder oberen Zeilenhälfte → Index i.
 */
export function insertIndexFromRowSlots(options: {
  prefix: readonly number[];
  heights: readonly number[];
  bandHeight: number;
  y: number;
  count: number;
}): number {
  const { prefix, heights, bandHeight, y, count } = options;
  if (count <= 0 || y <= 0) return 0;
  const total = prefix[count] ?? 0;
  if (y >= total) return count;

  let lo = 0;
  let hi = count;
  while (lo < hi) {
    const mid = (lo + hi) >> 1;
    if ((prefix[mid + 1] ?? 0) <= y) lo = mid + 1;
    else hi = mid;
  }
  const i = Math.min(lo, count - 1);
  const slotTop = prefix[i] ?? 0;
  const slotHeight = heights[i] ?? 0;
  const rowMid = slotTop + bandHeight + Math.max(0, slotHeight - bandHeight) / 2;
  return y < rowMid ? i : i + 1;
}

export function insertIndexFromPointerLayout(
  pointer: { x: number; y: number },
  scrollEl: HTMLElement,
  layout: OfferTableRowLayout,
): number {
  const scale = layout.scale > 0 ? layout.scale : 1;
  const scrollRect = scrollEl.getBoundingClientRect();
  const yScaled = pointer.y - scrollRect.top + scrollEl.scrollTop;
  const yUnscaled = (yScaled - layout.bodyOffsetScaled) / scale;
  return insertIndexFromRowSlots({
    prefix: layout.prefix,
    heights: layout.heights,
    bandHeight: layout.bandHeight,
    y: yUnscaled,
    count: layout.count,
  });
}
